import { Link } from "react-router-dom";
import { CalendarDays, CalendarPlus, Clock, History, MapPin, Plus, Search, Users } from "lucide-react";
import { Spinner } from "@/components/kit";
import { useEvents } from "@/hooks/queries";

const parse = (d) => new Date(d?.length <= 10 ? `${d}T00:00:00` : d);
const todayStr = () => new Date().toISOString().slice(0, 10);
const status = (d) => {
  const day = (d || "").slice(0, 10);
  const t = todayStr();
  return day === t ? "today" : day > t ? "upcoming" : "past";
};
const BADGE = {
  today: ["Today", "bg-[#ECFDF5] text-[#047857]"],
  upcoming: ["Upcoming", "bg-[#E5E5FD] text-[#0000EC]"],
  past: ["Past", "bg-[#F1F5F9] text-[#64748B]"],
};

const Stat = ({ icon: Icon, label, value, fg, bg }) => (
  <div className="t-card p-4 flex items-center gap-3">
    <span className="w-11 h-11 rounded-[12px] flex items-center justify-center" style={{ background: bg }}><Icon className="w-5 h-5" style={{ color: fg }} /></span>
    <span><span className="block text-[22px] font-extrabold leading-none">{value}</span><span className="text-[13px] font-semibold text-[#64748B]">{label}</span></span>
  </div>
);

function EventTile({ ev }) {
  const d = parse(ev.eventDate);
  const st = status(ev.eventDate);
  const [label, cls] = BADGE[st];
  const valid = !isNaN(d.getTime());
  return (
    <Link to={`/events/${ev.id}`} className="t-card p-5 flex flex-col gap-4 transition-[transform,box-shadow,border-color] hover:-translate-y-0.5 hover:border-[#0000EC]/30 hover:shadow-[0_14px_30px_-18px_rgba(0,0,236,.45)]" data-testid={`event-tile-${ev.id}`}>
      <div className="flex items-start gap-4">
        <span className={`w-16 shrink-0 rounded-[14px] overflow-hidden text-center ${st === "past" ? "bg-[#F1F5F9]" : "bg-[#E5E5FD]"}`}>
          <span className={`block py-1 text-[11px] font-bold uppercase text-white ${st === "past" ? "bg-[#94A3B8]" : "bg-[#0000EC]"}`}>{valid ? d.toLocaleDateString(undefined, { month: "short" }) : "—"}</span>
          <span className={`block pt-1 text-[24px] font-extrabold leading-none ${st === "past" ? "text-[#475569]" : "text-[#0000EC]"}`}>{valid ? d.getDate() : "?"}</span>
          <span className="block pb-1.5 text-[11px] font-semibold text-[#64748B]">{valid ? d.toLocaleDateString(undefined, { weekday: "short" }) : ""}</span>
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-[17px] font-bold leading-snug line-clamp-2">{ev.name}</h3>
            <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-bold ${cls}`}>{label}</span>
          </div>
          {ev.location && <p className="mt-1 flex items-center gap-1.5 text-[13px] text-[#64748B] truncate"><MapPin className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{ev.location}</span></p>}
          <p className="mt-1 text-[13px] text-[#94A3B8]">{valid ? d.getFullYear() : ""}</p>
        </div>
      </div>
      {ev.description && <p className="text-[14px] text-[#475569] line-clamp-2">{ev.description}</p>}
      <div className="mt-auto pt-3 border-t border-[#F1F5F9] flex items-center justify-between text-[13px]">
        <span className="inline-flex items-center gap-1.5 font-semibold text-[#334155]"><Users className="w-4 h-4 text-[#0000EC]" />{ev.contactsCount} contact{ev.contactsCount === 1 ? "" : "s"}</span>
        <span className="font-semibold text-[#0000EC]">View event →</span>
      </div>
    </Link>
  );
}

export default function EventsDesktop({ search, setSearch, filter, setFilter, list, loading, onCreate }) {
  const all = useEvents({ sort: "date" }).data ?? [];
  const upcoming = all.filter((e) => status(e.eventDate) !== "past").length;
  const people = all.reduce((n, e) => n + (e.contactsCount || 0), 0);
  return (
    <div className="px-10 py-8 max-w-[1400px] mx-auto fade-up" data-testid="events-desktop">
      <div className="flex items-end justify-between">
        <div><h1 className="text-[32px] font-extrabold leading-tight">Events</h1><p className="text-[14px] text-[#64748B]">Group the people you meet by where you met them.</p></div>
        <button className="t-btn t-btn-primary h-11 px-5 text-[15px]" onClick={onCreate} data-testid="events-create"><Plus className="w-5 h-5" />New Event</button>
      </div>
      <div className="grid grid-cols-4 gap-4 mt-6">
        <Stat icon={CalendarDays} label="Total events" value={all.length} fg="#0000EC" bg="#E5E5FD" />
        <Stat icon={Clock} label="Upcoming" value={upcoming} fg="#059669" bg="#ECFDF5" />
        <Stat icon={History} label="Past" value={all.length - upcoming} fg="#64748B" bg="#F1F5F9" />
        <Stat icon={Users} label="Contacts linked" value={people} fg="#EA580C" bg="#FFF7ED" />
      </div>
      <div className="t-card mt-6 p-3 flex items-center gap-3">
        <span className="relative flex-1 max-w-md">
          <Search className="absolute left-3.5 top-3 w-5 h-5 text-[#64748B]" />
          <input className="t-input h-11 pl-11 text-[14px] bg-white" placeholder="Search events by name or location" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="events-search" />
        </span>
        <div className="inline-grid grid-cols-3 p-1 rounded-[12px] bg-[#F1F5F9]">
          {["all", "upcoming", "past"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} className={`h-9 px-5 rounded-[10px] text-[14px] font-semibold capitalize transition-colors ${filter === f ? "bg-white text-[#0000EC] shadow-sm" : "text-[#475569]"}`} data-testid={`events-filter-${f}`}>{f}</button>
          ))}
        </div>
        <span className="ml-auto text-[13px] font-semibold text-[#64748B] pr-2" data-testid="events-count">{list.length} shown</span>
      </div>
      {loading ? <Spinner /> : (
        <div className="grid grid-cols-2 xl:grid-cols-3 gap-5 mt-6" data-testid="events-list">
          {list.map((ev) => <EventTile key={ev.id} ev={ev} />)}
          <button onClick={onCreate} className="rounded-[18px] border-2 border-dashed border-[#CBD5E1] min-h-[180px] flex flex-col items-center justify-center gap-2 text-[#475569] hover:border-[#0000EC] hover:text-[#0000EC] hover:bg-[#F5F5FF] transition-colors" data-testid="events-create-tile">
            <CalendarPlus className="w-8 h-8" /><span className="text-[15px] font-bold">Create a new event</span><span className="text-[13px] text-[#64748B]">Meetups, expos, conferences…</span>
          </button>
        </div>
      )}
    </div>
  );
}
